import { z } from 'zod';

import {
  PERFORMANCE_CLIENT_LISTS,
  PERFORMANCE_CLIENT_SORTS,
  PERFORMANCE_TRAINING_TYPES,
} from '../types/performance';
import { civilDaysBetween } from '../utils/date';
import { PickedCivilDayDto } from './civilDate';
import { Currency } from '../enums';

// A bare `?trainingTypes=booking` arrives as a string, not a one-element
// array — Express/Nest only produces an array for a REPEATED key (the FE
// repeats the key, `queryParamsFromString`). Same normalization as
// `scheduleSearch.ts`'s `kinds`.
const toArray = (value: unknown) => {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value;
  return typeof value === 'string' ? value.split(',') : [value];
};

/**
 * The longest window the Performance screens read in one request. PO
 * 08.10.2026 (Q8): a custom range may reach back to "max (total)"; the chosen
 * technical cap is 3650 inclusive days, not a promise of unrestricted all-time history. `visitsByDay` still carries
 * one row per day (≤ 3650), which the screens bucket into weeks or months.
 */
export const PERFORMANCE_RANGE_MAX_DAYS = 3650;

/** The page cap of the Clients & groups ranking. */
export const PERFORMANCE_CLIENTS_PAGE_MAX = 100;

/**
 * The inclusive civil-day range every Performance read takes, as a plain
 * object SHAPE. `PerformanceRangeQueryDto` is a `ZodEffects` once refined, so
 * it cannot be `.extend()`ed: a sibling DTO spreads this shape into its own
 * strict object and then applies BOTH range predicates below, in this order.
 */
export const PerformanceRangeShape = {
  from: PickedCivilDayDto,
  to: PickedCivilDayDto,
};

/** `from` is not after `to` (refine message `VALIDATION__EndBeforeStart`). */
export const isOrderedPerformanceRange = (query: {
  from: string;
  to: string;
}) => query.from <= query.to;

/** The inclusive range is shorter than `PERFORMANCE_RANGE_MAX_DAYS` (refine
 *  message `VALIDATION__InvalidRange`); an inverted range is left to the
 *  ordering predicate, so one bad query reports one error. */
export const isWithinPerformanceRange = (query: { from: string; to: string }) =>
  query.from > query.to ||
  civilDaysBetween(query.from, query.to) < PERFORMANCE_RANGE_MAX_DAYS;

/**
 * Task 6.3 — `GET organizations/:organizationId/performance/overview`.
 *
 * `from` and `to` are BARE civil days ('yyyy-MM-dd') of the ORGANIZATION's
 * zone, both INCLUSIVE: `from=2026-09-01&to=2026-09-30` is the whole of
 * September. A bare day, never an instant — the organization's zone is
 * resolved on the server, so the screen and every figure cut days the same
 * way. `.strict()` makes a smuggled `organizationId`/`tenantId`/`currency` a
 * refusal, never an override.
 */
export const PerformanceRangeQueryDto = z
  .object(PerformanceRangeShape)
  .strict()
  .refine(isOrderedPerformanceRange, {
    message: 'VALIDATION__EndBeforeStart',
    path: ['to'],
  })
  .refine(isWithinPerformanceRange, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  });

export type ApiPerformanceRangeQueryDto = z.infer<
  typeof PerformanceRangeQueryDto
>;

/**
 * PO 08.10.2026 (2.1–2.3) — `GET organizations/:organizationId/performance/clients`.
 * The same inclusive civil-day range as the Overview, plus the list, the
 * ranking, the page and the filters. Whether a money ranking is ALLOWED is
 * the controller's question (actor class), not the DTO's.
 */
export const PerformanceClientsQueryDto = z
  .object({
    currency: z.nativeEnum(Currency).optional(),
    from: PickedCivilDayDto,
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(PERFORMANCE_CLIENTS_PAGE_MAX)
      .default(25),
    list: z.enum(PERFORMANCE_CLIENT_LISTS).default('active'),
    membershipId: z.string().uuid().optional(),
    offset: z.coerce.number().int().min(0).default(0),
    order: z.enum(['asc', 'desc']).default('desc'),
    sort: z.enum(PERFORMANCE_CLIENT_SORTS).default('checkIns'),
    to: PickedCivilDayDto,
    trainerId: z.string().uuid().optional(),
    trainingTypes: z
      .preprocess(toArray, z.array(z.enum(PERFORMANCE_TRAINING_TYPES)).min(1))
      .optional(),
  })
  .strict()
  .refine(isOrderedPerformanceRange, {
    message: 'VALIDATION__EndBeforeStart',
    path: ['to'],
  })
  .refine(isWithinPerformanceRange, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  });

export type ApiPerformanceClientsQueryDto = z.infer<
  typeof PerformanceClientsQueryDto
>;
